import { AnimatePresence } from "framer-motion";
import {
  Briefcase,
  Wallet,
  CalendarDays,
  PawPrint,
  Cigarette,
  CigaretteOff,
  Users,
  ShieldCheck,
  Lock,
} from "lucide-react";
import { format } from "date-fns";
import { ExpandShell } from "@/components/swipe/listing_expand";
import BadgeVerified from "@/components/badges/badge_verified";

// The renter flavor of the Tinder-style profile expand, for listers browsing
// renters. Same shell (carousel, physics, docked Like / Nope), renter info
// chips instead of listing chips. LIMITED payloads ({first_name, budget_min,
// budget_max, about_short, locked: true}) keep occupation and references
// hidden until a match.

function Chip({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-night-tint px-3 py-1.5 text-xs font-semibold text-night-text">
      {Icon && <Icon size={14} className="shrink-0 text-brand-primary" />}
      {children}
    </span>
  );
}

function budgetLine(renter) {
  if (renter.budget_min || renter.budget_max) {
    const min = renter.budget_min ? `$${Number(renter.budget_min).toLocaleString()}` : "";
    const max = renter.budget_max ? `$${Number(renter.budget_max).toLocaleString()}` : "";
    if (min && max) return `Budget ${min} to ${max}/mo`;
    return `Budget ${min || `up to ${max}`}/mo`;
  }
  return `Budget $${Number(renter.budget || 0).toLocaleString()}/mo`;
}

function moveInLine(renter) {
  let from = "soon";
  try {
    if (renter.move_in) from = format(new Date(renter.move_in), "MMM d");
  } catch {
    from = renter.move_in || "soon";
  }
  return `Moving ${from}`;
}

export default function RenterExpand({ open, renter, onClose, onAction }) {
  return (
    <AnimatePresence>
      {open && renter && (
        <RenterExpandInner key={renter.id} renter={renter} onClose={onClose} onAction={onAction} />
      )}
    </AnimatePresence>
  );
}

function RenterExpandInner({ renter, onClose, onAction }) {
  const media = (renter.photos || [renter.photo]).filter(Boolean).map((src) => ({ type: "photo", src }));

  return (
    <ExpandShell
      onClose={onClose}
      onAction={onAction}
      media={media}
      testId="renter-expand"
      mediaOverlay={
        <h2 className="text-[28px] font-extrabold leading-none tracking-tight">
          {renter.name || renter.first_name}
          {renter.age ? <span className="ml-2 font-semibold text-white/85">{renter.age}</span> : null}
        </h2>
      }
    >
      {/* Badges */}
      {(renter.verified || renter.kyc_verified) && (
        <div className="flex flex-wrap items-center gap-2">
          {renter.verified && <BadgeVerified />}
          {renter.kyc_verified && <BadgeVerified label="ID verified" />}
        </div>
      )}

      {/* Icon info chips — locked fields stay hidden until a match */}
      <div data-testid="expand-rows" className="mt-4 flex flex-wrap gap-2">
        {renter.locked ? (
          <Chip icon={Lock}>Occupation unlocks on match</Chip>
        ) : (
          renter.occupation && <Chip icon={Briefcase}>{renter.occupation}</Chip>
        )}
        <Chip icon={Wallet}>{budgetLine(renter)}</Chip>
        {renter.move_in && <Chip icon={CalendarDays}>{moveInLine(renter)}</Chip>}
        <Chip icon={PawPrint}>{renter.pets || "No pets"}</Chip>
        {renter.smoking ? <Chip icon={Cigarette}>Smoker</Chip> : <Chip icon={CigaretteOff}>Nonsmoker</Chip>}
        <Chip icon={Users}>{renter.household || "Single"}</Chip>
        {renter.screening_complete && <Chip icon={ShieldCheck}>Screening complete</Chip>}
      </div>

      {/* About */}
      {(renter.about || renter.about_short || renter.bio) && (
        <p className="mt-4 text-sm leading-relaxed text-night-sub">
          {renter.about || renter.about_short || renter.bio}
        </p>
      )}

      {renter.locked && (
        <p className="mt-4 flex items-center gap-2 rounded-2xl bg-night px-3 py-2.5 text-xs font-medium text-night-sub">
          <Lock className="h-3.5 w-3.5 shrink-0" />
          Occupation and references unlock when you both match
        </p>
      )}
    </ExpandShell>
  );
}
